import 'reflect-metadata';

/**
 * Metadata describing one parameter of an `@AITool` method. Order in the
 * `parameters` array MUST match the method's positional signature — the
 * registry uses index lookup when mapping the model's named args back to
 * positional arguments at dispatch time.
 */
export interface ToolParameterMeta {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description: string;
  required?: boolean;
  enum?: string[];
  default?: unknown;
}

export interface ToolMeta {
  name: string;
  description: string;
  parameters: ToolParameterMeta[];
  methodName: string;
}

const TOOLS_METADATA_KEY = Symbol('ai:tools');

/**
 * Marks an async class method as a tool the AI assistant can call. The chosen
 * `name` is the public identifier the model uses. `parameters` describes the
 * method's positional arguments — order MUST match the method signature.
 *
 * Tool providers are then DI services registered into `AiToolRegistryService`
 * via the `AI_TOOL_PROVIDERS` multi-provider token (see tool-registry.service.ts).
 *
 * @example
 *   @AITool('search_contacts', 'Search HCPs by name', [
 *     { name: 'search_term', type: 'string', description: 'name fragment', required: true },
 *     { name: 'limit',       type: 'number', description: 'max results',  default: 10 },
 *   ])
 *   async searchContacts(search_term: string, limit = 10) { ... }
 */
export function AITool(name: string, description: string, parameters: ToolParameterMeta[] = []) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const tools: ToolMeta[] = Reflect.getMetadata(TOOLS_METADATA_KEY, target.constructor) || [];
    tools.push({ name, description, parameters, methodName: propertyKey });
    Reflect.defineMetadata(TOOLS_METADATA_KEY, tools, target.constructor);
    return descriptor;
  };
}

/** Return every `@AITool`-decorated method on a class (via its constructor). */
export function getRegisteredTools(targetClass: any): ToolMeta[] {
  return Reflect.getMetadata(TOOLS_METADATA_KEY, targetClass) || [];
}

/**
 * Foundry / OpenAI Responses-API function-tool shape — the schema Foundry
 * stores on the agent's `definition.tools[]`. Note: Responses API uses the
 * top-level `name`/`description`/`parameters`, NOT the Chat-Completions
 * nested `function: { name, ... }` form.
 */
export interface FoundryToolDefinition {
  type: 'function';
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, { type: string; description: string; enum?: string[]; default?: unknown }>;
    required?: string[];
  };
}

export function toFoundryToolDefinition(tool: ToolMeta): FoundryToolDefinition {
  const properties: FoundryToolDefinition['parameters']['properties'] = {};
  const required: string[] = [];
  for (const p of tool.parameters) {
    const prop: { type: string; description: string; enum?: string[]; default?: unknown } = {
      type: p.type,
      description: p.description,
    };
    if (p.enum?.length) prop.enum = p.enum;
    if (p.default !== undefined) prop.default = p.default;
    properties[p.name] = prop;
    if (p.required) required.push(p.name);
  }
  return {
    type: 'function',
    name: tool.name,
    description: tool.description,
    parameters: { type: 'object', properties, ...(required.length ? { required } : {}) },
  };
}
